'use client'

import { getActivationTrackingSessionId } from '@/app/(public)/components/activation-tracking-client'
import useActivationImpression from '@/app/(public)/components/use-activation-impression'
import { Button } from '@/components/ui/button'
import { buildTrackedActivationHref } from '@/lib/activations/utils'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

function getActivationDescription(description, index = 0) {
  if (Array.isArray(description)) {
    const selectedIndex = index % description.length
    return description[selectedIndex]
  }

  return description
}

function ActivationCarouselSlide({ activation, index, isHydrated }) {
  const pathname = usePathname() || '/'
  const placement = 'home-page-carousel'
  const trackingRef = useActivationImpression({
    activationId: activation.id,
    placement,
  })

  // Keep the first render deterministic and add the client-only session id
  // once hydration has completed.
  const trackedHref = buildTrackedActivationHref({
    activationId: activation.id,
    fromPage: pathname,
    placement,
    sessionId: isHydrated ? getActivationTrackingSessionId() : null,
  })

  return (
    <div
      ref={trackingRef}
      className="carousel-slide group min-w-0 flex-[0_0_100%] px-[24px] desktop-lg:px-0"
    >
      <a href={trackedHref} className="block w-full">
        <div className="carousel-slide-item h-auto! w-full">
          <div className="relative overflow-hidden rounded-lg border border-neutral-blue-700 font-inter">
            <div className="relative">
              <Image
                alt={`${activation.name} logo`}
                height={298}
                width={1410}
                sizes="(min-width: 1536px) 1296px, (min-width: 1280px) calc(100vw - 96px), (min-width: 768px) calc(100vw - 64px), calc(100vw - 48px)"
                priority={index === 0}
                className="rounded-lg rounded-t-md object-contain tablet:object-cover"
                src={activation.bannerImgUrl || '/img/default/header.webp'}
              />
              <div className="z-1 bg-linear-to-t absolute left-0 top-0 h-full w-full from-[#010810] to-transparent to-20% tablet:hidden" />
            </div>
            <div className="z-1 bg-linear-to-r absolute left-0 top-0 hidden h-full w-full from-[#010810] to-[#01081149] tablet:block desktop-xs:via-[#010810e9] desktop-xs:via-20% desktop-xs:to-[#01081000]"></div>
            <div className="z-2 h-full w-full tablet:absolute tablet:left-0 tablet:top-0 tablet:flex">
              <div className="flex flex-col px-7 pb-7 pt-4 tablet:my-auto tablet:w-full tablet:px-12 tablet:pb-0 tablet:pt-0 desktop-xs:px-[87px]">
                <h6 className="gf-gradient-text w-fit text-p-1 font-semibold">
                  {activation.metadata.title}
                </h6>
                <h3 className="mt-3 w-fit text-p-2 font-bold mobile:text-p-3 tablet:text-h-4 desktop-md:text-h-3">
                  {activation.name}
                </h3>
                <h3 className="mt-2 text-p-2 font-bold tablet:text-h-4 desktop-xs:mt-0 desktop-md:text-h-3">
                  {getActivationDescription(
                    activation.metadata.description,
                    index,
                  )}
                </h3>
                <Button
                  variant="outline"
                  asChild
                  className="px-0! group-hover:bg-white! group-hover:text-black! tablet:px-6! mt-[26px] w-full transition-all duration-300 ease-in-out tablet:mt-1 tablet:w-fit desktop-xs:mt-[26px]"
                >
                  <div>{activation.metadata.buttonText}</div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>
  )
}

export default function AllActivationsCarousel({ activations }) {
  const [isHydrated, setIsHydrated] = useState(false)
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [
      Autoplay({
        delay: 8000,
        stopOnMouseEnter: true,
        stopOnInteraction: false,
      }),
    ],
  )
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState([])

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )
  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  )
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <div className="mx-[-24px] overflow-hidden">
      <section className="relative w-full">
        {/* Navigation Buttons */}
        {activations?.length > 1 && (
          <div className="absolute left-0 right-0 top-1/2 z-10 hidden -translate-y-1/2 transform justify-between px-7 tablet:flex desktop-xs:px-8">
            <button
              onClick={scrollPrev}
              className="backdrop-blur-xs flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-all hover:bg-white/30"
              aria-label="Previous slide"
            >
              <ArrowLeft className="size-6" />
            </button>
            <button
              onClick={scrollNext}
              className="backdrop-blur-xs flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-all hover:bg-white/30"
              aria-label="Next slide"
            >
              <ArrowRight className="size-6" />
            </button>
          </div>
        )}

        <div className="tablet:overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {activations?.map((activation, index) => (
              <ActivationCarouselSlide
                key={activation.id}
                activation={activation}
                index={index}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>

        {/* Dots */}
        {scrollSnaps.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center gap-2">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(
                  'h-2 w-2 rounded-full transition-all',
                  index === selectedIndex
                    ? 'w-6 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/75',
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
